import React, { useState, useEffect } from 'react';
import IosCard from '../../components/iOSCard';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../../firebase'; 
import * as XLSX from 'xlsx'; // ✅ إضافة مكتبة الإكسل
import { 
    collection, 
    addDoc, 
    getDocs, 
    query, 
    orderBy, 
    deleteDoc, 
    doc,
    getDoc, // ✅ تم إضافة getDoc لجلب مستندات الطلاب الحقيقية
    serverTimestamp 
} from "firebase/firestore";
import { 
    TicketIcon, PlusIcon, ArrowDownTrayIcon, 
    CheckBadgeIcon, CurrencyDollarIcon,
    XMarkIcon, SparklesIcon, TrashIcon, CheckCircleIcon
} from '@heroicons/react/24/outline';

const TokenManager = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [filter, setFilter] = useState('all');
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generatedCode, setGeneratedCode] = useState(""); // ✅ لتخزين الكود الأخير للعرض الملكي
    
    const [numToGen, setNumToGen] = useState(1);
    const [selectedDuration, setSelectedDuration] = useState('كورس أول');

    useEffect(() => {
        const fetchTokens = async () => {
            try {
                const q = query(collection(db, "tokens"), orderBy("createdAt", "desc"));
                const querySnapshot = await getDocs(q);
                
                // 🔥 [SECTION: AL-BARON_DEVELOPMENT] - نظام جلب الأسماء وتصنيف الحالات الملكي لعام 2026 🛡️ 🔥
                const tokensList = [];
                
                // جلب كل مستندات الطلاب بضربة واحدة لتسريع عملية المطابقة ومنع البطء
                const usersSnapshot = await getDocs(collection(db, "users"));
                const usersMap = {};
                usersSnapshot.forEach(uDoc => {
                    usersMap[uDoc.id] = uDoc.data(); // حفظ البيانات بالـ uid مالتهم
                });

                for (const docSnapshot of querySnapshot.docs) {
                    const tokenData = docSnapshot.data();
                    let studentName = "غير مستخدم";
                    let tokenStatusLabel = "جديد | غير مستخدم"; // الحالة الافتراضية للتوكن

                    const rawUid = (tokenData.usedBy || "").toString().trim();

                    // 1️⃣ أولاً: استخراج وتحديد الاسم الحقيقي للطالب
                    if (tokenData.status === "used" || tokenData.status === "sold") {
                        if (rawUid.includes("@")) {
                            studentName = rawUid;
                        } 
                        else if (rawUid && usersMap[rawUid]) {
                            studentName = usersMap[rawUid].name || usersMap[rawUid].email || rawUid;
                        } 
                        else if (rawUid && rawUid.length > 0) {
                            studentName = rawUid;
                        } else {
                            studentName = "طالب متميز";
                        }
                    }

                   // 2️⃣ ثانياً: شجرة الشروط الملكية لتصنيف "الحالة برمجياً" 💎
                    // الربط المباشر بين حالة الكود وحالة حساب الطالب في StudentManager
                    if (tokenData.status === "available") {
                        tokenStatusLabel = "جديد | غير مستخدم";
                    } 
                    else if (tokenData.status === "used" || tokenData.status === "sold") {
                        // الفحص الملكي: هل الطالب موقوف (blocked) في ملف StudentManager؟
                        if (rawUid && usersMap[rawUid] && usersMap[rawUid].status === 'blocked') {
                            tokenStatusLabel = "منتهي الصلاحية متوقف";
                        } 
                        // إذا جان الكود مستخدم ولكن الطالب فعال
                        else {
                            tokenStatusLabel = "مستخدم | شغال";
                        }
                    } else if (tokenData.status === "expired") {
                        tokenStatusLabel = "منتهي الصلاحية";
                    }

                    tokensList.push({
                        id: docSnapshot.id,
                        ...tokenData,
                        usedByName: studentName,        // الاسم الحقيقي المصفى
                        customStatusLabel: tokenStatusLabel // حقل الحالة المطور الجديد للواجهة والـ Excel
                    });
                }
                setTokens(tokensList);
                // 🔥 [END of SECTION: AL-BARON_DEVELOPMENT] 🔥
            } catch (error) {
                console.error("Error fetching tokens: ", error);
            }
        };
        fetchTokens();
    }, []);

    // ✅ [SECTION: AL-BARON_EXCEL_REPORT] - دالة التقرير الملكي المحدثة 2026 (إضافة اسم المشترك والتسلسل والحالات الديناميكية)
    const exportToExcel = (data) => {
        if (data.length === 0) {
            alert("ضلعي، ماكو أكواد حالياً حتى أحملها!");
            return;
        }

        const excelData = data.map((t, index) => {
            // فحص دقيق لطباعة نص تاريخ التفعيل بالإكسل حسب الحالة الملكية
            let activationDateText = 'لم يفعل بعد';
            if (t.customStatusLabel === "منتهي الصلاحية متوقف" || t.customStatusLabel === "منتهي الصلاحية") {
                activationDateText = t.activatedAt?.toDate ? t.activatedAt.toDate().toLocaleString('ar-IQ') : 'منتهي/موقوف';
            } else if (t.activatedAt?.toDate) {
                activationDateText = t.activatedAt.toDate().toLocaleString('ar-IQ');
            }

            return {
                'ت': data.length - index, // رقم التسلسل من الأحدث للأقدم
                'اسم المشترك': t.status === 'available' ? '---' : (t.usedByName || 'غير معرف'), // اسم الطالب المربوط بالكود
                'كود التفعيل': t.code,
                'نوع الكورس': t.duration,
                'السعر': t.price + " د.ع",
                'الحالة': t.customStatusLabel, // 🔄 التعديل الملكي لتصنيف الحالات الديناميكية بالإكسل
                'تاريخ الإنشاء': t.createdAt?.toDate ? t.createdAt.toDate().toLocaleString('ar-IQ') : '---',
                'تاريخ التفعيل': activationDateText
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        const workbook_name = "الأكواد";
        XLSX.utils.book_append_sheet(workbook, worksheet, workbook_name);
        
        // ضبط عرض الأعمدة تلقائياً لجمالية الملف
        worksheet['!cols'] = [{ wch: 5 }, { wch: 25 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 25 }, { wch: 25 }];
        
        XLSX.writeFile(workbook, `أكواد_نور_أكاديمي_${new Date().toLocaleDateString('ar-IQ')}.xlsx`);
    };

    const handleGenerate = async () => {
        setLoading(true);
        let lastCode = "";
        try {
            for (let i = 0; i < numToGen; i++) {
                const prefix = selectedDuration === 'كورس أول' ? 'SEM1' : selectedDuration === 'كورس ثاني' ? 'SEM2' : 'FULL';
                const randomCode = `${prefix}-${Math.floor(1000 + Math.random() * 9000)}-${Math.random().toString(36).substring(2, 5).toUpperCase()}`;
                lastCode = randomCode;
                
                const newToken = {
                    code: randomCode,
                    duration: selectedDuration,
                    status: "available",
                    createdAt: serverTimestamp(),
                    price: selectedDuration === 'كورسين (كامل)' ? "300,000" : "150,000"
                };

                await addDoc(collection(db, "tokens"), newToken);
            }

            setGeneratedCode(lastCode); // ✅ حفظ الكود للعرض
            setIsModalOpen(false);
            setShowSuccess(true); // ✅ تفعيل المودال الوسطي الفخم
            
        } catch (e) {
            alert("خطأ في الحفظ: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    const deleteToken = async (id) => {
        if (window.confirm("هل أنت متأكد من حذف هذا الكود؟")) {
            try {
                await deleteDoc(doc(db, "tokens", id));
                setTokens(tokens.filter(t => t.id !== id));
            } catch (error) {
                alert("فشل الحذف: " + error.message);
            }
        }
    };

    // ✅ [SECTION: COPY_AND_CLOSE_FUNCTION] - إرجاع الدالة المفقودة للحفظ والنسخ السريع
    const copyAndClose = () => {
        if (generatedCode) {
            navigator.clipboard.writeText(generatedCode);
        }
        setShowSuccess(false);
        window.location.reload(); 
    };

    return (
        <div style={{ fontFamily: "'Cairo', sans-serif" }} className="space-y-8 animate-in fade-in duration-700 h-full flex flex-col relative text-right" dir="rtl">
            
            {/* 🚀 مودال الكود الجديد الملكي */}
            <AnimatePresence>
                {showSuccess && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[10000] flex items-center justify-center p-4 backdrop-blur-xl bg-black/10">
                        <motion.div initial={{ y: 50, scale: 0.9 }} animate={{ y: 0, scale: 1 }} exit={{ y: 50, opacity: 0 }} className="ios-glass max-w-sm w-full p-10 rounded-[50px] border-white border-2 shadow-2xl text-center space-y-6 bg-white/95">
                            <TicketIcon className="w-16 h-16 text-blue-500 mx-auto" />
                            <h3 className="text-2xl font-black text-gray-900 font-cairo">كود جديد جاهز!</h3>
                            <p className="text-xs text-gray-500 font-bold font-cairo">تم توليد {numToGen} أكواد بنجاح، إليك آخر كود:</p>
                            <div className="p-5 bg-blue-50 rounded-3xl border-2 border-dashed border-blue-200 shadow-inner">
                                <span className="text-2xl font-mono font-black text-blue-700 tracking-wider select-all">{generatedCode}</span>
                            </div>
                            <button onClick={copyAndClose} className="w-full py-4 bg-gray-900 text-white rounded-3xl font-black shadow-xl hover:scale-[1.02] active:scale-95 transition-all font-cairo">نسخ وإغلاق</button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* 1️⃣ بار الإجراءات والفلاتر */}
            <div className="flex flex-wrap gap-4 items-center justify-between ios-glass p-4 border-white/30 shadow-lg shrink-0">
                <div className="flex gap-4">
                    <button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-6 py-3 bg-[#007AFF] text-white rounded-2xl font-black shadow-lg shadow-blue-500/30 hover:scale-105 transition-all active:scale-95 font-cairo">
                        <PlusIcon className="w-5 h-5" /> توليد أكواد جديدة
                    </button>
                    <button 
                        onClick={() => exportToExcel(tokens)}
                        className="flex items-center gap-2 px-6 py-3 bg-white/80 text-gray-700 rounded-2xl font-black shadow-md hover:bg-white transition-all border border-white active:scale-95 font-cairo"
                    >
                        <ArrowDownTrayIcon className="w-5 h-5 text-emerald-600" /> تحميل Excel
                    </button>
                </div>
                <div className="flex p-1 bg-gray-200/50 rounded-2xl backdrop-blur-md border border-white/20">
                    {[ {id:'all', n:'الكل'}, {id:'available', n:'المتوفرة'}, {id:'sold_used', n:'المباعة'} ].map((f) => (
                        <button key={f.id} onClick={() => setFilter(f.id)} className={`px-6 py-2 rounded-xl text-xs font-black transition-all font-cairo ${filter === f.id ? 'bg-white shadow-md text-[#007AFF]' : 'text-gray-500'}`}>
                            {f.n}
                        </button>
                    ))}
                </div>
            </div>

            {/* 2️⃣ بطاقات الإحصائيات (تحديث: دعم sold و used) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 shrink-0">
                <IosCard title="أكواد متوفرة" color="blue" icon={<TicketIcon className="w-6 h-6"/>}>
                    <p className="text-3xl font-black text-[#007AFF] font-cairo">{tokens.filter(t => t.status === 'available').length}</p>
                </IosCard>
                <IosCard title="أكواد مباعة / منتهية" color="emerald" icon={<CheckBadgeIcon className="w-6 h-6"/>}>
                    <p className="text-3xl font-black text-[#34C759] font-cairo">{tokens.filter(t => t.status === 'sold' || t.status === 'used' || t.status === 'expired').length}</p>
                </IosCard>
                <IosCard title="الأرباح المتوقعة" color="orange" icon={<CurrencyDollarIcon className="w-6 h-6"/>}>
                    <p className="text-3xl font-black text-[#FF9500] font-cairo">
                        {tokens.filter(t => t.status === 'sold' || t.status === 'used').reduce((acc, curr) => acc + parseInt(curr.price.replace(',', '')), 0).toLocaleString()} <small className="text-sm font-bold text-gray-400">د.ع</small>
                    </p>
                </IosCard>
            </div>

            {/* 3️⃣ استعراض الأكواد (مع عرض اسم المشترك والحالات الديناميكية) */}
            <div className="flex-1 min-h-0">
                <IosCard title="إدارة مفاتيح التفعيل" icon={<SparklesIcon />} color="purple" className="h-full flex flex-col">
                    <div className="custom-scroll overflow-y-auto pr-2 h-full max-h-[calc(100vh-550px)] text-right">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-1">
                            <AnimatePresence>
                                {tokens.filter(t => {
                                    if (filter === 'all') return true;
                                    if (filter === 'available') return t.status === 'available';
                                    if (filter === 'sold_used') return t.status === 'sold' || t.status === 'used' || t.status === 'expired';
                                    return true;
                                }).map((token) => (
                                    <motion.div key={token.id} layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                                        className="ios-glass p-6 relative group overflow-hidden border border-white/50 shadow-lg rounded-[28px] hover:shadow-2xl transition-all"
                                    >
                                        <div className="flex justify-between items-start mb-4">
                                            <div className={`px-3 py-1 rounded-full text-[9px] font-black font-cairo ${
                                                token.duration === 'كورسين (كامل)' ? 'bg-purple-100 text-purple-600' : 
                                                token.duration === 'كورس أول' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'
                                            }`}>
                                                {token.duration}
                                            </div>
                                            <button onClick={() => deleteToken(token.id)} className="text-red-400 opacity-0 group-hover:opacity-100 transition-all hover:scale-110">
                                                <TrashIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                        <h3 className="text-lg font-black text-gray-800 tracking-widest mb-2 font-mono select-all font-cairo">{token.code}</h3>
                                        
                                        {/* عرض اسم المشترك الحقيقي المترجم بدقة */}
                                        {token.status !== 'available' && (
                                            <p className="text-[10px] font-bold text-blue-600 mb-2 truncate">المشترك: {token.usedByName}</p>
                                        )}

                                        <div className="pt-4 border-t border-white/30 flex justify-between items-center">
                                            <span className="text-sm font-black text-[#007AFF] font-cairo">{token.price} د.ع</span>
                                            
                                            {/* 🔄 تلوين الحالات بشكل ديناميكي صارم حسب نوع الفحص برعاية البارون */}
                                            <span className={`px-2 py-1 rounded-lg text-[9px] font-black font-cairo ${ 
                                                token.customStatusLabel === 'جديد | غير مستخدم' ? 'bg-emerald-50 text-emerald-600' : 
                                                token.customStatusLabel === 'مستخدم | شغال' ? 'bg-blue-50 text-blue-600' : 
                                                token.customStatusLabel === 'منتهي الصلاحية متوقف' ? 'bg-red-100 text-red-600 font-bold border border-red-200' :
                                                'bg-amber-100 text-amber-600'
                                            }`}>
                                                {token.customStatusLabel}
                                            </span>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </div>
                </IosCard>
            </div>

            {/* 4️⃣ نافذة التوليد الرئيسية */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsModalOpen(false)} className="absolute inset-0 bg-black/20 backdrop-blur-sm" />
                        <motion.div initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 20 }} className="bg-white/90 backdrop-blur-2xl w-full max-w-md p-8 shadow-2xl relative z-10 border border-white/50 rounded-[40px]" >
                            <div className="flex justify-between items-center mb-8 text-right" dir="rtl">
                                <h3 className="text-2xl font-black text-gray-800 flex items-center gap-2 font-cairo">
                                    <SparklesIcon className="w-6 h-6 text-[#007AFF]" /> توليد مفاتيح
                                </h3>
                                <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all"><XMarkIcon className="w-6 h-6 text-gray-400" /></button>
                            </div>
                            <div className="space-y-6 text-right" dir="rtl">
                                <div>
                                    <label className="block text-sm font-black text-gray-500 mb-2 mr-2 uppercase font-cairo">عدد الأكواد</label>
                                    <input type="number" value={numToGen} onChange={(e) => setNumToGen(e.target.value)} className="w-full p-4 rounded-2xl bg-white border border-gray-100 outline-none focus:ring-2 focus:ring-blue-400 font-black text-lg text-center font-cairo" />
                                </div>
                                <div>
                                    <label className="block text-sm font-black text-gray-500 mb-2 mr-2 uppercase font-cairo">نوع الكورس</label>
                                    <div className="grid grid-cols-1 gap-2">
                                        {['كورس أول', 'كورس ثاني', 'كورسين (كامل)'].map((option) => (
                                            <button key={option} onClick={() => setSelectedDuration(option)} className={`w-full p-4 rounded-2xl border text-right font-black text-sm transition-all flex justify-between items-center font-cairo ${selectedDuration === option ? 'border-blue-500 bg-blue-50 text-blue-600' : 'border-gray-100 bg-white text-gray-500'}`}>
                                                {option}
                                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedDuration === option ? 'border-blue-500 bg-blue-500 shadow-md' : 'border-gray-200'}`}>{selectedDuration === option && <div className="w-2 h-2 bg-white rounded-full" />}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <button onClick={handleGenerate} disabled={loading} className="w-full py-5 bg-[#007AFF] text-white rounded-[24px] font-black text-lg shadow-xl shadow-blue-500/30 hover:scale-[1.02] active:scale-95 transition-all mt-4 flex justify-center items-center font-cairo">
                                    {loading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : "توليد الآن 🚀"}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* ✅ التوست السفلي الاحتياطي */}
            <AnimatePresence>
                {showSuccess && (
                    <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="fixed bottom-10 left-1/2 -translate-x-1/2 bg-[#34C759] text-white px-8 py-4 rounded-full font-black shadow-2xl z-[2000] flex items-center gap-3 border border-white/20 backdrop-blur-md font-cairo">
                        <CheckCircleIcon className="w-6 h-6" /> تم التوليد والحفظ في السحابة!
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default TokenManager;